import React, { useState } from 'react';
import { Table } from 'reactstrap';
import { toast } from 'react-toastify';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import RefreshIcon from 'mdi-react/RefreshIcon';
import ShareVariantIcon from 'mdi-react/ShareVariantIcon';
import CancelIcon from 'mdi-react/CancelIcon';
import LoadingIcon from 'mdi-react/LoadingIcon';
import Api from '../../../../util/api';
import { partial } from '../../../../util/translation/translation';
import FormCard from '../../../../shared/components/custom/FormCard';
import SquareIconButton from '../../../../shared/components/custom/SquareIconButton';
import FormModal from '../../../../shared/components/custom/FormModal';
import PersonCardForm from '../../../tickets/externalProviders/components/PersonCardForm';

const STATUSES = ['ACTIVE', 'SENDING', 'RECEIVING'];

const formatDate = (iso) => {
  if (!iso) return '–';
  return new Date(iso).toLocaleString([], {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ticketField = (ticket, key) => ticket?.extraData?.[key] || ticket?.[key];

// One event: a row that opens to its tickets
const EventTickets = ({
  event,
  status,
  defaultOpen,
  onShare,
  onCancelShare,
}) => {
  const ut = partial('UserTickets');
  const [isOpen, setIsOpen] = useState(defaultOpen);

  // Who the ticket goes to / comes from, or the provider for own tickets
  const otherPartyLabel = {
    RECEIVING: ut('receivingFrom'),
    SENDING: ut('sentTo'),
    ACTIVE: ut('provider'),
  }[status];
  const otherParty = (ticket) => {
    if (status === 'RECEIVING') {
      return ticket?.ticketSharing?.fromUser?.phoneNumber;
    }
    if (status === 'SENDING') {
      return ticket?.ticketSharing?.toUser?.phoneNumber;
    }
    return ticket.externalProvider;
  };

  return (
    <div className={`ut-event${isOpen ? ' ut-event--open' : ''}`}>
      <button
        type="button"
        className="ut-event__head"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <ChevronRightIcon size={20} className="ut-event__chevron" />
        <span className="ut-event__name">{event.name}</span>
        <span className="ut-event__when">
          {formatDate(event.startsAt)} – {formatDate(event.endsAt)}
        </span>
        <span className="qb-chip">
          {ut('ticketCount', { count: event.tickets.length })}
        </span>
      </button>
      {isOpen && (
        <Table responsive striped className="table--roomy table--stack mb-0">
          <thead>
            <tr>
              <th>{ut('field')}</th>
              <th>{ut('row')}</th>
              <th>{ut('seat')}</th>
              <th>{ut('priceGroup')}</th>
              <th>{otherPartyLabel}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {event.tickets.map((ticket) => (
              <tr key={ticket.id}>
                <td className="table-stack__primary">
                  <strong className="font-weight-500">
                    {ticketField(ticket, 'field') || '–'}
                  </strong>
                </td>
                <td data-label={ut('row')}>
                  {ticketField(ticket, 'row') || '–'}
                </td>
                <td data-label={ut('seat')}>
                  {ticketField(ticket, 'seat') || '–'}
                </td>
                <td data-label={ut('priceGroup')}>
                  {ticketField(ticket, 'priceGroup') || '–'}
                </td>
                <td data-label={otherPartyLabel}>
                  {otherParty(ticket) || '–'}
                </td>
                <td className="table-stack__actions">
                  <div className="icon-actions justify-content-end">
                    {ticket.ticketSharing && (
                      <span className="qb-chip ut-pending">
                        {ut('pendingShare')}
                      </span>
                    )}
                    {ticket.ticketSharing ? (
                      <SquareIconButton
                        icon={<CancelIcon />}
                        title={ut('cancelShare')}
                        tone="danger"
                        id={`cancel-share-${ticket.id}`}
                        onClick={() => onCancelShare(ticket)}
                      />
                    ) : (
                      ticket.shareable && (
                        <SquareIconButton
                          icon={<ShareVariantIcon />}
                          title={ut('share')}
                          id={`share-${ticket.id}`}
                          onClick={() => onShare(ticket)}
                        />
                      )
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

const ExternalTickets = ({ user, phone, provider }) => {
  const ut = partial('UserTickets');
  const [ticketGroups, setTicketGroups] = useState({
    ACTIVE: [],
    SENDING: [],
    RECEIVING: [],
  });
  const [syncing, setSyncing] = useState(false);
  const [synced, setSynced] = useState(false);
  const [shareTicket, setShareTicket] = useState(null);

  // This user's tickets with the provider (fetched live)
  const syncTickets = async () => {
    setSyncing(true);
    try {
      const response = await Api.tickets.getAdminUserTickets(
        user,
        provider,
        phone
      );
      setTicketGroups(response || { ACTIVE: [], SENDING: [], RECEIVING: [] });
    } catch {
      toast.error(ut('fetchError'));
    } finally {
      setSyncing(false);
      setSynced(true);
    }
  };

  // On a hard reload the user (and so the phone) loads after this mounts
  React.useEffect(() => {
    if (phone) syncTickets();
    // eslint-disable-next-line
  }, [user, phone]);

  const handleCancelShare = async (ticket) => {
    try {
      await Api.tickets.handleShareTickets({
        ticketSharings: [{ id: ticket.ticketSharing.id, action: 'CANCELLED' }],
      });
      toast.success(ut('shareCancelled'));
      // Reload this user's tickets (not the logged-in admin's own)
      syncTickets();
    } catch {
      toast.error(ut('shareCancelError'));
    }
  };
  const closeShareModal = () => setShareTicket(null);

  const sortEvents = (events) =>
    [...events].sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));

  const allEvents = STATUSES.flatMap((status) => ticketGroups?.[status] || []);
  const ticketTotal = allEvents.reduce(
    (sum, event) => sum + (event.tickets?.length || 0),
    0
  );

  const statusLabel = {
    ACTIVE: ut('active'),
    SENDING: ut('sent'),
    RECEIVING: ut('receiving'),
  };

  return (
    <div className="ut">
      <div className="ut__bar">
        <span className="ut__summary">
          {syncing || !phone ? (
            <>
              <LoadingIcon className="stats-spinner ut__spinner" aria-hidden />
              {ut('checking')}
            </>
          ) : (
            synced &&
            ut('summary', { tickets: ticketTotal, events: allEvents.length })
          )}
        </span>
        <SquareIconButton
          icon={<RefreshIcon />}
          title={ut('refresh')}
          id={`refresh-tickets-${provider}`}
          disabled={syncing || !phone}
          onClick={syncTickets}
        />
      </div>

      {synced && !syncing && !allEvents.length && (
        <div className="ut__empty">{ut('noTickets')}</div>
      )}

      {STATUSES.map((status) => {
        const events = ticketGroups?.[status] || [];
        if (!events.length) return null;
        return (
          <FormCard
            key={status}
            title={
              <>
                {statusLabel[status]}
                <span className="form-card__count">{events.length}</span>
              </>
            }
            bodyClassName="form-card__body--flush"
            className="mb-3"
          >
            {sortEvents(events).map((event, index) => (
              <EventTickets
                key={event.id}
                event={event}
                status={status}
                defaultOpen={status === 'ACTIVE' && index === 0}
                onShare={setShareTicket}
                onCancelShare={handleCancelShare}
              />
            ))}
          </FormCard>
        );
      })}

      <FormModal
        isOpen={!!shareTicket}
        onClose={closeShareModal}
        title={ut('shareTitle')}
        subtitle={
          shareTicket
            ? `${shareTicket.eventName} · ${ut('seatLine', {
                field: ticketField(shareTicket, 'field') || '–',
                row: ticketField(shareTicket, 'row') || '–',
                seat: ticketField(shareTicket, 'seat') || '–',
              })}`
            : null
        }
      >
        <div className="qm-body">
          {shareTicket && (
            <PersonCardForm
              ticket={shareTicket}
              fromUid={user}
              onClose={closeShareModal}
              // The form reloads the admin's own tickets afterwards; here
              // the user's tickets are reloaded instead
              setTicketGroups={() => syncTickets()}
            />
          )}
        </div>
      </FormModal>
    </div>
  );
};

export default ExternalTickets;
